import { ArrowUpOutlined } from '@ant-design/icons'
import { useFormatter } from '@api/useFormatter'
import { useOrders } from '@api/useOrders'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { paneModes } from '@constants/orderWizard'
import {
  addCommasToNumber,
  GraviButton,
  Horizontal,
  MaterialIcon,
  NothingMessage,
  Texto,
  Vertical,
} from '@gravitate-js/excalibrr'
import { Alert, Button, Checkbox, Popover, Select, Tooltip } from 'antd'
import React, { useMemo, useState } from 'react'

import { useDestinationsContext } from '../Contexts/DestinationsContext'
import { useOrderContainersContext } from '../Contexts/OrderContainersContext'
import { useWizardMaster } from '../Contexts/WizardMasterContext'
import { isTruckLoaded } from '../util'
import { BackhaulPreview } from './DisplayPane/DisplayComponents/Backhaul/BackhaulPreview'
import { OrderFlagAlert } from './DisplayPane/DisplayComponents/OrderFlagAlert'
import { PocketAllocation } from './DisplayPane/DisplayComponents/Pockets/PocketAllocation'
import { ProductAllocation } from './DisplayPane/DisplayComponents/Pockets/ProductAllocation'
import { OrderPreview } from './DisplayPane/OrderPreview'
import { SupplyOptionSelect } from './DisplayPane/SupplyOptionSelect'
import { TankDrilldown } from './DisplayPane/TankDrilldown'

export function DisplayPane({ updateGridOrder }) {
  const {
    volumes,
    maxVolumes,
    setVolumes,
    setMaxVolumes,
    compartments,
    constructCompartments,
    trailerMetadata,
    trailerOverride,
    setTrailerOverride,
    setHasChangedTrailer,
    productVolumeTotal,
    productMaxVolume,
    productGroupsApi,
  } = useOrderContainersContext()

  const { paneMode, wizardOrder, isBackhaul, config, currentEntryMode, handleEntryModeChange } = useWizardMaster()

  const [flags, setFlags] = useState(getFlags(wizardOrder))
  const setNewFlagOrder = (order) => setFlags(getFlags(order))

  const showFlags = wizardOrder && flags.length > 0 && paneMode === paneModes.TANKIMPACT

  const { scopes } = useSecurityContext()
  const { orderDestinations } = useDestinationsContext()
  const defaultTrailerConfig = useMemo(() => orderDestinations?.[0]?.trailer_config, [orderDestinations])

  const canChangeTrailer = useMemo(
    () =>
      config?.multi_truck &&
      [paneModes.TANKIMPACT, paneModes.PRODUCTSELECT].includes(paneMode) &&
      currentEntryMode === 'by_compartment',
    [config, paneMode, currentEntryMode]
  )
  const showTrailerConfigAndCompartments = useMemo(() => orderDestinations.length !== 0, [orderDestinations])

  const isCarrierOrder = wizardOrder?.carrier || wizardOrder?.manager

  const canEditOrder = useMemo(() => {
    return (
      scopes?.create_order &&
      !isBackhaul &&
      !['complete', 'in progress'].includes(wizardOrder?.state) &&
      (wizardOrder?.state !== 'assigned' || (wizardOrder?.state === 'assigned' && isCarrierOrder)) &&
      (wizardOrder?.id || paneMode !== paneModes.TANKIMPACT)
    )
  }, [scopes, wizardOrder])

  const handleTrailerChange = (trailer) => {
    if (canChangeTrailer) {
      setHasChangedTrailer(true)
      setTrailerOverride(trailer)
    }
  }

  const { toByCompartmentMutation } = useOrders()
  const toggleDisabled = useMemo(() => {
    const isLoaded = wizardOrder && (wizardOrder?.state === 'complete' || wizardOrder?.trip_status?.includes('drop'))
    return isLoaded || paneMode !== paneModes.PRODUCTSELECT || productGroupsApi.volumeStatus === 'missing'
  }, [wizardOrder?.trip_status, wizardOrder?.state, paneMode, productGroupsApi?.volumeStatus])

  async function handleModeChange({ mode, fill_trailer }) {
    if (mode === 'by_compartment' && productGroupsApi.compartments.length) {
      setTrailerOverride(defaultTrailerConfig)
      const compartments = await toByCompartmentMutation.mutateAsync({
        trailer_config: defaultTrailerConfig,
        fill_trailer,
        compartments: productGroupsApi.compartments,
      })
      if (compartments) {
        constructCompartments({ compartments })
        const defaults = trailerMetadata?.default_volumes?.find(
          (t) => t.configuration === defaultTrailerConfig
        )?.volumes
        const maxes = trailerMetadata?.max_volumes?.find((t) => t.configuration === defaultTrailerConfig)?.volumes
        setVolumes(defaults)
        setMaxVolumes(maxes ?? [])
      }
    } else if (mode === 'by_product' && compartments.some((c) => !!c)) {
      productGroupsApi.setProductGroupsFromCompartments(compartments)
    }
    handleEntryModeChange(mode)
  }

  function ModeSwitchButton({ mode, buttonText }) {
    const [fill_trailer, set_fill_trailer] = useState(false)
    const [isPopoverOpen, setIsPopoverOpen] = useState(false)

    if (
      (mode === 'by_compartment' && productGroupsApi?.compartments.length === 0) ||
      (mode === 'by_product' && compartments.every((c) => !c))
    ) {
      return (
        <GraviButton
          buttonText={buttonText}
          theme2={currentEntryMode === mode || (mode === 'by_compartment' && !currentEntryMode)}
          data-cy={`productGroups-modeSwitch-${mode}`}
          onClick={() => handleModeChange({ mode, fill_trailer })}
        />
      )
    }

    return (
      <Popover
        trigger='click'
        open={isPopoverOpen}
        onOpenChange={(visible) => setIsPopoverOpen(visible)}
        placement='bottomRight'
        content={
          currentEntryMode === mode ? null : mode === 'by_compartment' ? (
            <Vertical style={{ gap: '0.25rem', minWidth: 300 }}>
              <Texto category='h6'>
                {' '}
                Change Order To <i>By Compartment?</i>
              </Texto>
              <Texto>Default Trailer Config: {defaultTrailerConfig}</Texto>

              <Horizontal justifyContent='space-between' alignItems='center' className='mt-3'>
                <Vertical>
                  <Horizontal alignItems='center' style={{ gap: '0.5rem' }}>
                    <Checkbox checked={fill_trailer} onChange={(e) => set_fill_trailer(e.target.checked)} />
                    <Tooltip title='If selected, the order volumes will be modified to fill the trailer'>
                      <div>
                        <Texto> Fill Trailer? </Texto>
                      </div>
                    </Tooltip>
                  </Horizontal>
                </Vertical>
                <Horizontal style={{ gap: '0.25rem' }}>
                  <GraviButton buttonText='Dismiss' onClick={() => setIsPopoverOpen(false)} />
                  <GraviButton buttonText='Confirm' onClick={() => handleModeChange({ mode, fill_trailer })} theme2 />
                </Horizontal>
              </Horizontal>
            </Vertical>
          ) : (
            <Vertical style={{ gap: '0.25rem', minWidth: 300 }}>
              <Texto category='h6'>
                Change Order To <i>By Product?</i>
              </Texto>

              <Horizontal justifyContent='flex-end' alignItems='center' style={{ gap: '0.25rem' }} className='mt-3'>
                <GraviButton buttonText='Dismiss' onClick={() => setIsPopoverOpen(false)} />
                <GraviButton buttonText='Confirm' onClick={() => handleModeChange({ mode })} theme2 />
              </Horizontal>
            </Vertical>
          )
        }
      >
        <GraviButton
          disabled={toggleDisabled}
          buttonText={buttonText}
          theme1={currentEntryMode === mode || (mode === 'by_compartment' && !currentEntryMode)}
          data-cy={`productGroups-modeSwitch-${mode}`}
          onClick={() => setIsPopoverOpen(true)}
        />
      </Popover>
    )
  }

  return (
    <div className='display-pane'>
      {isBackhaul ? (
        <div className='display-contents'>
          <BackhaulPreview />
        </div>
      ) : (
        <>
          {showTrailerConfigAndCompartments && (
            <>
              <Horizontal
                bg='elevated'
                border='all'
                justifyContent='space-between'
                className='px-3 py-2'
                alignItems='center'
              >
                <VolumeStatusIndicator
                  volume={productVolumeTotal}
                  min={productGroupsApi?.minimumVolume}
                  max={productMaxVolume}
                  shouldValidate={currentEntryMode === 'by_product'}
                  productGroupsApi={productGroupsApi}
                />
                {canChangeTrailer && (
                  <TrailerConfigSelect
                    disabled={!canEditOrder}
                    value={trailerOverride}
                    options={trailerMetadata?.names}
                    onChange={handleTrailerChange}
                  />
                )}
                <Horizontal alignItems='center'>
                  <Button.Group>
                    <ModeSwitchButton buttonText='By Compartment' mode='by_compartment' />
                    <ModeSwitchButton buttonText='By Product' mode='by_product' />
                  </Button.Group>
                </Horizontal>
              </Horizontal>
              {isTruckLoaded(wizardOrder?.trip_status) && paneMode === paneModes.PRODUCTSELECT && (
                <Alert type='warning' message='Truck is loaded. Drop volumes have to match loaded volume.' />
              )}
              {currentEntryMode === 'by_product' ? (
                <Horizontal className='pocket-allocation'>
                  <ProductAllocation />
                </Horizontal>
              ) : (
                <Horizontal className='pocket-allocation'>
                  {compartments.map((c, index) => {
                    return (
                      <PocketAllocation
                        container={c}
                        key={index}
                        index={index}
                        volumes={volumes}
                        maxVolumes={maxVolumes}
                      />
                    )
                  })}
                </Horizontal>
              )}
            </>
          )}
          {showFlags && (
            <OrderFlagAlert
              flags={flags}
              order={wizardOrder}
              updateOrder={setNewFlagOrder}
              updateGridOrder={updateGridOrder}
            />
          )}
          <div className='display-contents flex-1'>
            <DisplayContents paneMode={paneMode} order={wizardOrder} />
          </div>
        </>
      )}
    </div>
  )
}

function TrailerConfigSelect({ value, options, onChange, disabled }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: '1rem',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Texto category='p2'>TRAILER CONFIGURATION</Texto>
      <Tooltip title={disabled ? "Trailer config can't be changed for this order" : null}>
        <Select value={value} style={{ width: 240 }} onChange={onChange} disabled={disabled} placeholder='None'>
          {options.map((option) => (
            <Select.Option value={option} key={option.toLowerCase()}>
              {option}
            </Select.Option>
          ))}
        </Select>
      </Tooltip>
    </div>
  )
}

function VolumeStatusIndicator({ volume, min, max, shouldValidate = true, productGroupsApi }) {
  const { formatVolume } = useFormatter()

  const volumeStatus = useMemo(() => {
    if (productGroupsApi.volumeStatus === 'diff_to_loaded') return { status: 'diff_to_loaded', diff: 0 }
    if (volume < min && !productGroupsApi.allowShortLoadEntry) return { status: 'under', diff: min - volume }
    if (volume > max) return { status: 'over', diff: volume - max }
    return { status: 'within', diff: 0 }
  }, [volume, min, max, productGroupsApi.allowShortLoadEntry, productGroupsApi.volumeStatus])

  const isVolumeInvalid = ['over', 'under', 'diff_to_loaded', 'missing'].includes(volumeStatus.status)

  return (
    <Horizontal alignItems='baseline'>
      <Texto
        category='h5'
        className={`ml-2 ${isVolumeInvalid && shouldValidate ? 'text-error' : ''}`}
        weight='bolder'
        data-cy='productGroups-volumeDisplay-total'
      >
        {addCommasToNumber(volume)}
      </Texto>
      <Texto category='h5' className='ml-2 mr-1' weight='bold'>
        /
      </Texto>
      <Texto category='h5' className='text-uppercase' data-cy='productGroups-volumeDisplay-max'>
        {formatVolume(max, true)}
      </Texto>
      {isVolumeInvalid && shouldValidate && (
        <Texto
          category='p2'
          className='ml-2 text-error'
          style={{ fontStyle: 'italic' }}
          weight='normal'
          data-cy='productGroups-volumeDisplay-validationMessage'
        >
          {volumeStatus.status === 'diff_to_loaded' &&
            `One or more product volumes do not match what was loaded. ${Object.entries(
              productGroupsApi.mismatchedVolumes ?? {}
            )
              ?.map(([key, value]) => `${key}: ${value}`)
              .join(', ')}`}
          {volumeStatus.status === 'over' && `Order exceeds max volume of ${formatVolume(max)}`}
          {volumeStatus.status === 'under' && `Order doesn't meet min volume of ${formatVolume(min, true)}`}
        </Texto>
      )}
    </Horizontal>
  )
}

function DisplayContents() {
  const { resetVolumes, compartments } = useOrderContainersContext()
  const { paneMode, wizardOrder, isShortLoad, setIsShortLoad, currentEntryMode } = useWizardMaster()
  const { selectedTank, selectedManifold, orderDestinations } = useDestinationsContext()
  const showProductDrilldown = !!selectedManifold || !!selectedTank
  const selectedStore = orderDestinations.find((d) =>
    selectedTank
      ? selectedTank.store_number === d.store_number
      : selectedManifold
        ? selectedManifold.store_number === d.store_number
        : false
  )

  const resetCompartmentVolumes = () => {
    resetVolumes()
    setIsShortLoad(false)
  }

  switch (paneMode) {
    case paneModes.TYPESELECT:
      return <NothingMessage title='Select an Order Type' message='Select an order type to begin selecting products.' />

    case paneModes.PRODUCTSELECT:
      if (selectedStore?.monitoring_strategy === 'No Readings (Call-In)') {
        return <NothingMessage title='No Readings' message='Call in site.' />
      }

      return (
        <>
          {currentEntryMode === 'by_compartment' && compartments?.some((c) => c?.targets?.some((t) => t.modified)) && (
            <Alert
              className='custom-volume-alert '
              style={{ borderRadius: 0 }}
              message={
                <Horizontal verticalCenter gap={8}>
                  <MaterialIcon style={{ color: 'var(--theme-color-1' }} icon='compare' fontSize={18} />
                  <Texto weight='bold' category='p2'>
                    Custom Compartment Volumes
                  </Texto>
                  <Texto className='pt-1' category='label'>
                    The compartment volumes on this order have been modified.
                  </Texto>
                </Horizontal>
              }
              action={
                !compartments.some((c) => c && c.targets.length > 1) && (
                  <Button onClick={() => resetCompartmentVolumes()} size='small' type='text'>
                    RESET
                  </Button>
                )
              }
            />
          )}
          {showProductDrilldown ? (
            <TankDrilldown />
          ) : (
            <NothingMessage title='Select Order Products' message='Click the tank row to view tank analytics.' />
          )}
        </>
      )

    case paneModes.SUPPLYFILTERS:
      return <SupplyOptionSelect />
    case paneModes.TANKIMPACT:
      if (!wizardOrder) return null
      if (selectedStore?.monitoring_strategy === 'No Readings (Call-In)') {
        return <OrderPreview />
      }
      return showProductDrilldown ? <TankDrilldown showHeader /> : <OrderPreview />
    default:
      return <div className='p-3'>Invalid Pane Mode</div>
  }
}

function getFlags(order = {}) {
  const { flags: flagsFromBE = [] } = order
  const hasUnsafeSplitFlagFromBE = flagsFromBE.some((flag) => flag.type === 'unsafe_split')
  const filteredFlags = getFilteredFlags()
  const newFlagsPreSave = getNewFlagsPreSave()
  return [...filteredFlags, ...newFlagsPreSave]

  function getFilteredFlags() {
    const { drops } = order
    if (hasUnsafeSplitFlagFromBE) {
      const sources = []
      const firstCompartmentIndex = 0
      drops[0].details.forEach((d) => d.sources.forEach((s) => sources.push(s.compartment_index)))
      if (!sources.includes(firstCompartmentIndex)) {
        const index = flagsFromBE.findIndex((f) => f.type === 'unsafe_split')
        flagsFromBE.splice(index, 1)
      }
    }
    return flagsFromBE
  }

  function getNewFlagsPreSave() {
    const { drops } = order
    const hasMultipleDrops = drops?.length > 1
    const newFlags = []

    if (hasMultipleDrops) {
      const sources = []
      const fistCompartMentIndex = 0
      drops[0].details.forEach((d) => d.sources.forEach((s) => sources.push(s.compartment_index)))
      if (sources.includes(fistCompartMentIndex) && !hasUnsafeSplitFlagFromBE) newFlags.push(unsafeOrderFlag)
    }
    return newFlags
  }
}

const unsafeOrderFlag = {
  name: 'Unsafe Split',
  type: 'unsafe_split',
  severity: 'warning',
  reason_code: null,
  details: [
    {
      label: 'Warning',
      value: 'It may be unsafe to unload the first compartment of product at the first store in a multi-store order.',
      date: null,
    },
  ],
}
